import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Chip, Grid, Tabs, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TableSortLabel, TextField, InputAdornment,
  CircularProgress, Alert, Button, IconButton, Collapse, FormControl, InputLabel,
  Select, MenuItem, LinearProgress, Tooltip, Card, CardContent, Pagination,
} from '@mui/material';
import {
  Search as SearchIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  ArrowBack as ArrowBackIcon, Build as BuildIcon, Person as PersonIcon,
  AttachMoney as AttachMoneyIcon, Schedule as ScheduleIcon, Assignment as AssignmentIcon,
  Business as BusinessIcon, MoveToInbox as MoveToInboxIcon, Block as BlockIcon,
  Save as SaveIcon, Download as DownloadIcon,
} from '@mui/icons-material';
import {
  getAftercareDashboard, getAftercareWorkOrders, getAftercareClients,
  getAftercareClient, getAftercareTechnicians, getAftercareCoverage,
  getClientWarranty, updateClientWarranty, updateAfterCareWarranty,
  getClosedJobs,
  getAftercareIntakeQueue, dismissAftercareIntake,
} from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AftercareOperationsView from '../components/AftercareOperationsView';
import PageSearchBar from '../components/PageSearchBar';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtCurrency = (v) => {
  const n = parseFloat(v) || 0;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
};
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const fmtNum = (v, dec = 1) => {
  const n = parseFloat(v) || 0;
  return n.toLocaleString('en-US', { maximumFractionDigits: dec });
};

// Status mapping per Pat Kross (2026-05-08):
//   0=New, 3=Invoiced/Closed, 6=Complete — awaiting billing, 8=Billed (legacy)
const STATUS_CONFIG = {
  0: { label: 'New', color: '#e65100', bg: '#fff3e0' },
  3: { label: 'Closed', color: '#2e7d32', bg: '#e8f5e9' },
  6: { label: 'Awaiting Billing', color: '#1565c0', bg: '#e3f2fd' },
  8: { label: 'Billed', color: '#616161', bg: '#f5f5f5' },
};
const StatusChip = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, color: '#999', bg: '#eee' };
  return <Chip label={cfg.label} size="small" sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 700, fontSize: '0.7rem' }} />;
};

// ─── Summary Card ─────────────────────────────────────────────────────────────
const SummaryCard = ({ icon, label, value, color = '#0D2C54', sub, onClick, action }) => (
  <Paper elevation={2} onClick={onClick} sx={{ p: 2, borderLeft: `4px solid ${color}`, height: '100%', cursor: onClick ? 'pointer' : 'default', position: 'relative', transition: 'all 0.2s', '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: 4 } : {} }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
      {icon}
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
    <Typography variant="h4" fontWeight="bold" color={color}>{value}</Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    {action && <Box sx={{ position: 'absolute', top: 8, right: 8 }} onClick={(e) => e.stopPropagation()}>{action}</Box>}
  </Paper>
);

// Revenue YTD download button
const RevenueYTDDownload = ({ fy }) => {
  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/aftercare/revenue-ytd?format=csv', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`Download failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `AfterCare_Revenue_YTD_${fy || ''}.csv`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch (e) { console.error(e); alert('Download failed: ' + e.message); }
  };
  return (
    <Tooltip title="Download Revenue YTD detail (CSV)">
      <IconButton size="small" onClick={handleDownload} sx={{ color: '#1565c0' }}>
        <DownloadIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

// ─── Dashboard Tab ────────────────────────────────────────────────────────────
function DashboardView({ onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    getAftercareDashboard()
      .then(r => setData(r.data))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Sort state for dashboard tables
  const [tcSort, setTcSort] = useState({ col: 'wo_count', dir: 'desc' });
  const [rwSort, setRwSort] = useState({ col: 'WRKORDNBR', dir: 'desc' });

  const makeSorter = (sort, setSort) => (col) => {
    setSort(s => ({ col, dir: s.col === col && s.dir === 'desc' ? 'asc' : 'desc' }));
  };
  const doSort = (arr, sort, numCols = []) => [...arr].sort((a, b) => {
    let av = a[sort.col], bv = b[sort.col];
    if (numCols.includes(sort.col)) { av = parseFloat(av) || 0; bv = parseFloat(bv) || 0; }
    else { av = (av || '').toString().toLowerCase(); bv = (bv || '').toString().toLowerCase(); }
    if (av < bv) return sort.dir === 'asc' ? -1 : 1;
    if (av > bv) return sort.dir === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const s = data.summary;
  const sortedClients = doSort(data.topClients, tcSort, ['wo_count', 'open_count', 'total_billed']);
  const sortedRecent = doSort(data.recent, rwSort, ['WRKORDNBR', 'WRKORDAMT', 'STATUS']);
  return (
    <Box>
      {/* AfterCare intake banner — lightweight count + jump-to-tab.
          Tom 2026-05-20. Full table lives on the Intake Queue tab. */}
      <IntakeQueueBanner onOpen={() => onNavigate('intake-queue')} />

      {data.fiscal_year && (
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip size="small" label={`Fiscal Year ${data.fiscal_year}`} sx={{ bgcolor: '#0D2C54', color: '#fff', fontWeight: 700 }} />
          <Typography variant="caption" color="text.secondary">All YTD figures are fiscal-year-to-date (Oct 1 → Sep 30), matching Sage.</Typography>
        </Box>
      )}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <SummaryCard
            icon={<AssignmentIcon sx={{ color: '#e65100' }} />}
            label="Open Work Orders"
            value={s.open_count}
            color="#e65100"
            sub={`${s.new_count} new · ${s.scheduled_count} awaiting billing${typeof s.open_count_carryover === 'number' && s.open_count_carryover > 0 ? ` · ${s.open_count_carryover} carryover from prior FY` : ''}`}
            onClick={() => onNavigate('work-orders')}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryCard icon={<BuildIcon sx={{ color: '#2e7d32' }} />} label="Completed YTD" value={s.completed_ytd} color="#2e7d32" sub={`${s.new_ytd} new WOs created · ${s.invoiced_ytd} invoiced`} onClick={() => onNavigate('work-orders')} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryCard
            icon={<AttachMoneyIcon sx={{ color: '#1565c0' }} />}
            label="Revenue YTD"
            value={fmtCurrency(s.revenue_ytd)}
            color="#1565c0"
            sub={`${s.customers_ytd} customers`}
            onClick={() => onNavigate('clients')}
            action={<RevenueYTDDownload fy={data.fiscal_year} />}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <SummaryCard icon={<ScheduleIcon sx={{ color: '#7b1fa2' }} />} label="Labor Hours YTD" value={fmtNum(s.hours_ytd)} color="#7b1fa2" sub={`${s.techs_ytd} technicians`} onClick={() => onNavigate('technicians')} />
        </Grid>
      </Grid>

      {/* Top clients & Recent WOs side by side */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>🔥 Top Clients YTD</Typography>
            <Table size="small">
              <TableHead><TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.75rem' } }}>
                {[{ k: 'client_name', l: 'Client' }, { k: 'wo_count', l: 'WOs', a: 'center' }, { k: 'open_count', l: 'Open', a: 'center' }, { k: 'total_billed', l: 'Billed', a: 'right' }].map(c => (
                  <TableCell key={c.k} align={c.a}><TableSortLabel active={tcSort.col === c.k} direction={tcSort.col === c.k ? tcSort.dir : 'asc'} onClick={() => makeSorter(tcSort, setTcSort)(c.k)}>{c.l}</TableSortLabel></TableCell>
                ))}
              </TableRow></TableHead>
              <TableBody>
                {sortedClients.map(c => (
                  <TableRow key={c.ar_code} hover>
                    <TableCell><Typography variant="body2" fontWeight="medium">{c.client_name}</Typography></TableCell>
                    <TableCell align="center"><Chip label={c.wo_count} size="small" color="primary" /></TableCell>
                    <TableCell align="center">{c.open_count > 0 ? <Chip label={c.open_count} size="small" color="warning" /> : '—'}</TableCell>
                    <TableCell align="right"><Typography variant="body2">{fmtCurrency(c.total_billed)}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>🕐 Recent Work Orders</Typography>
            <Table size="small">
              <TableHead><TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.75rem' } }}>
                {[{ k: 'WRKORDNBR', l: 'WO#' }, { k: 'client_name', l: 'Client' }, { k: 'notes_preview', l: 'Service Note' }, { k: 'STATUS', l: 'Status' }].map(c => (
                  <TableCell key={c.k}><TableSortLabel active={rwSort.col === c.k} direction={rwSort.col === c.k ? rwSort.dir : 'asc'} onClick={() => makeSorter(rwSort, setRwSort)(c.k)}>{c.l}</TableSortLabel></TableCell>
                ))}
              </TableRow></TableHead>
              <TableBody>
                {sortedRecent.map(w => (
                  <TableRow key={w.WRKORDNBR} hover>
                    <TableCell><Typography variant="body2" color="primary.main" fontWeight="bold">{w.WRKORDNBR}</Typography></TableCell>
                    <TableCell><Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>{w.client_name}</Typography></TableCell>
                    <TableCell><Typography variant="caption" noWrap sx={{ maxWidth: 180 }}>{(w.notes_preview || w.problem || '').trim().substring(0, 60) || '—'}</Typography></TableCell>
                    <TableCell><StatusChip status={w.STATUS} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>

      {/* Clients needing attention (open WOs) + Revenue by region */}
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>⚠️ Needs Attention</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Clients with open work orders — oldest first</Typography>
            {data.topClients.filter(c => c.open_count > 0).sort((a, b) => new Date(a.last_visit) - new Date(b.last_visit)).slice(0, 10).map(c => (
              <Box key={c.ar_code} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                <Box>
                  <Typography variant="body2" fontWeight="medium">{c.client_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{fmtDate(c.last_visit)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Chip label={`${c.open_count} open`} size="small" color="warning" />
                  <Typography variant="caption" color="text.secondary">{fmtCurrency(c.total_billed)}</Typography>
                </Box>
              </Box>
            ))}
            {data.topClients.filter(c => c.open_count > 0).length === 0 && (
              <Typography variant="body2" color="text.secondary">No clients with open WOs in top 10</Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>📍 Revenue by Region (YTD)</Typography>
            {data.byCallType.filter(ct => ct.call_type && ct.call_type.startsWith('Service Call')).map(ct => {
              const region = ct.call_type.replace('Service Call - ', '').replace('Service Call ', '');
              return (
                <Box key={ct.call_type} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                  <Typography variant="body2">{region}</Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">{ct.cnt} calls</Typography>
                    <Typography variant="body2" fontWeight="bold" color="success.main">{fmtCurrency(ct.revenue)}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

// ─── Work Orders Tab ──────────────────────────────────────────────────────────
function WorkOrdersView() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filters, setFilters] = useState({ status: 'open', search: '' });
  const [woPage, setWoPage] = useState(1);
  const woPerPage = 50;
  const [sort, setSort] = useState('DATEENTER');
  const [dir, setDir] = useState('DESC');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, sort, dir, limit: 300 };
      if (!params.status || params.status === 'all') delete params.status;
      if (!params.search) delete params.search;
      const r = await getAftercareWorkOrders(params);
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filters, sort, dir]);

  useEffect(() => { const t = setTimeout(load, filters.search ? 400 : 0); return () => clearTimeout(t); }, [load]);

  const handleSort = (col) => {
    if (sort === col) setDir(d => d === 'ASC' ? 'DESC' : 'ASC');
    else { setSort(col); setDir('DESC'); }
  };

  const thStyle = { fontWeight: 700, fontSize: '0.75rem', color: '#fff' };
  const sortLabel = (col, label) => (
    <TableSortLabel active={sort === col} direction={sort === col ? dir.toLowerCase() : 'asc'} onClick={() => handleSort(col)}
      sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#ccc !important' } }}>{label}</TableSortLabel>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <PageSearchBar
          placeholder="Search client, AR code, WO#, or notes…"
          value={filters.search}
          onChange={(v) => setFilters(f => ({ ...f, search: v }))}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filters.status} label="Status" onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            <MenuItem value="open">Open (New + Awaiting Billing)</MenuItem>
            <MenuItem value="new">New Only</MenuItem>
            <MenuItem value="scheduled">Awaiting Billing Only</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
            <MenuItem value="billed">Billed</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">{total.toLocaleString()} results</Typography>
      </Box>

      {loading ? <LinearProgress /> : (<>
        <TableContainer component={Paper} elevation={2}>
          <Table size="small">
            <TableHead><TableRow sx={{ bgcolor: '#0D2C54' }}>
              <TableCell sx={thStyle}>{sortLabel('WRKORDNBR', 'WO#')}</TableCell>
              <TableCell sx={thStyle}>{sortLabel('NAME', 'Client')}</TableCell>
              <TableCell sx={thStyle}>Service Notes</TableCell>
              <TableCell sx={thStyle}>Tech</TableCell>
              <TableCell sx={thStyle}>{sortLabel('DATEENTER', 'Date')}</TableCell>
              <TableCell sx={thStyle}>Closed</TableCell>
              <TableCell sx={{ ...thStyle, textAlign: 'right' }}>{sortLabel('WRKORDAMT', 'Amount')}</TableCell>
              <TableCell sx={{ ...thStyle, textAlign: 'right' }}>{sortLabel('ACTLABORHOURS', 'Hours')}</TableCell>
              <TableCell sx={thStyle}>Status</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {rows.slice((woPage - 1) * woPerPage, woPage * woPerPage).map(w => (
                <React.Fragment key={w.WRKORDNBR}>
                  <TableRow hover sx={{ cursor: 'pointer', bgcolor: expanded === w.WRKORDNBR ? 'action.selected' : undefined }}
                    onClick={() => setExpanded(e => e === w.WRKORDNBR ? null : w.WRKORDNBR)}>
                    <TableCell><Typography variant="body2" color="primary.main" fontWeight="bold">{w.WRKORDNBR}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 200 }}>{w.client_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{w.ar_code}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ maxWidth: 280, display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden' }}>
                        {(w.notes_preview || '').trim() || w.call_type || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption" noWrap>{w.tech_name || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{fmtDate(w.DATEENTER)}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{fmtDate(w.DATECLOSED)}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2" fontWeight={w.WRKORDAMT > 0 ? 'bold' : 'normal'} color={w.WRKORDAMT > 0 ? 'success.main' : 'text.secondary'}>{w.WRKORDAMT > 0 ? fmtCurrency(w.WRKORDAMT) : '—'}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2">{w.ACTLABORHOURS > 0 ? fmtNum(w.ACTLABORHOURS) : '—'}</Typography></TableCell>
                    <TableCell><StatusChip status={w.STATUS} /></TableCell>
                  </TableRow>
                  <TableRow><TableCell colSpan={10} sx={{ p: 0, border: 0 }}>
                    <Collapse in={expanded === w.WRKORDNBR} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '2px solid #0D2C54' }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>📝 Service Notes</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', maxHeight: 300, overflow: 'auto', mb: 1.5 }}>
                          {(w.notes_preview || '').trim() || 'No notes on this work order.'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {w.call_type && <Chip label={w.call_type} size="small" color="primary" variant="outlined" />}
                          {w.problem && <Chip label={w.problem} size="small" variant="outlined" />}
                          {w.center_name && <Chip label={w.center_name} size="small" variant="outlined" />}
                          {w.SCHEDDATE && <Chip label={`Scheduled: ${fmtDate(w.SCHEDDATE)}`} size="small" color="info" variant="outlined" />}
                          {w.DATERUN && <Chip label={`Run: ${fmtDate(w.DATERUN)}`} size="small" variant="outlined" />}
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell></TableRow>
                </React.Fragment>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={10} sx={{ textAlign: 'center', py: 4 }}><Typography color="text.secondary">No work orders found</Typography></TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
        {rows.length > woPerPage && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, px: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Showing {((woPage - 1) * woPerPage) + 1}–{Math.min(woPage * woPerPage, rows.length)} of {rows.length}
            </Typography>
            <Pagination count={Math.ceil(rows.length / woPerPage)} page={woPage} onChange={(e, p) => setWoPage(p)} color="primary" size="small" />
          </Box>
        )}
      </>)}
    </Box>
  );
}

// ─── Clients Tab ──────────────────────────────────────────────────────────────
function ClientsView({ initialArcust = null, onClearInitial = () => {} }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('wo_count');
  const [dir, setDir] = useState('DESC');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  // Auto-load drilldown when caller seeds an arcust (e.g. from Operations tab click).
  useEffect(() => {
    if (!initialArcust) return;
    let cancelled = false;
    (async () => {
      setSelected(initialArcust);
      setDetail(null);
      try {
        const r = await getAftercareClient(initialArcust);
        if (!cancelled) setDetail(r.data);
      } catch (e) { if (!cancelled) setDetail(null); }
      onClearInitial(); // one-shot — don't re-fire on remount
    })();
    return () => { cancelled = true; };
  }, [initialArcust]); // eslint-disable-line react-hooks/exhaustive-deps
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getAftercareClients({ search: search || undefined });
      setClients(r.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [search]);

  useEffect(() => { const t = setTimeout(load, search ? 400 : 0); return () => clearTimeout(t); }, [load]);

  // Client-side sorting
  const sorted = [...clients].sort((a, b) => {
    let av = a[sort], bv = b[sort];
    if (sort === 'wo_count' || sort === 'open_count' || sort === 'total_billed' || sort === 'total_hours') {
      av = parseFloat(av) || 0; bv = parseFloat(bv) || 0;
    } else if (sort === 'last_visit') {
      av = av ? new Date(av).getTime() : 0; bv = bv ? new Date(bv).getTime() : 0;
    } else {
      av = (av || '').toString().toLowerCase(); bv = (bv || '').toString().toLowerCase();
    }
    if (av < bv) return dir === 'ASC' ? -1 : 1;
    if (av > bv) return dir === 'ASC' ? 1 : -1;
    return 0;
  });

  const loadDetail = async (arcust) => {
    setSelected(arcust);
    setDetailLoading(true);
    try {
      const r = await getAftercareClient(arcust);
      setDetail(r.data);
    } catch (e) { console.error(e); setDetail(null); }
    setDetailLoading(false);
  };

  const handleSort = (col) => {
    if (sort === col) setDir(d => d === 'ASC' ? 'DESC' : 'ASC');
    else { setSort(col); setDir('DESC'); }
  };

  const thStyle = { fontWeight: 700, fontSize: '0.75rem', color: '#fff' };
  const sl = (col, label) => (
    <TableSortLabel active={sort === col} direction={sort === col ? dir.toLowerCase() : 'asc'} onClick={() => handleSort(col)}
      sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#ccc !important' } }}>{label}</TableSortLabel>
  );

  if (selected && detail) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => { setSelected(null); setDetail(null); }} sx={{ mb: 2 }}>Back to Clients</Button>
        {detailLoading ? <CircularProgress /> : (
          <Box>
            <Paper elevation={2} sx={{ p: 3, mb: 2, borderLeft: '4px solid #0D2C54' }}>
              <Typography variant="h5" fontWeight="bold">{detail.summary.client_name}</Typography>
              <Typography variant="body2" color="text.secondary">AR: {detail.summary.ar_code}</Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6} sm={2}><Typography variant="caption" color="text.secondary">Total WOs</Typography><Typography variant="h6" fontWeight="bold">{detail.summary.total_wos}</Typography></Grid>
                <Grid item xs={6} sm={2}><Typography variant="caption" color="text.secondary">Open</Typography><Typography variant="h6" fontWeight="bold" color="warning.main">{detail.summary.open_wos}</Typography></Grid>
                <Grid item xs={6} sm={2}><Typography variant="caption" color="text.secondary">Total Billed</Typography><Typography variant="h6" fontWeight="bold" color="success.main">{fmtCurrency(detail.summary.total_billed)}</Typography></Grid>
                <Grid item xs={6} sm={2}><Typography variant="caption" color="text.secondary">Labor Hours</Typography><Typography variant="h6" fontWeight="bold">{fmtNum(detail.summary.total_hours)}</Typography></Grid>
                <Grid item xs={6} sm={2}><Typography variant="caption" color="text.secondary">First Visit</Typography><Typography variant="body1">{fmtDate(detail.summary.first_visit)}</Typography></Grid>
                <Grid item xs={6} sm={2}><Typography variant="caption" color="text.secondary">Last Visit</Typography><Typography variant="body1">{fmtDate(detail.summary.last_visit)}</Typography></Grid>
                <Grid item xs={6} sm={2}><Typography variant="caption" color="text.secondary">Service Plan</Typography>
                  {detail.summary.service_plan ? (
                    <Chip label={detail.summary.service_plan} sx={{
                      fontWeight: 700, mt: 0.5,
                      bgcolor: detail.summary.service_plan === 'VIP' ? '#7b1fa2' : detail.summary.service_plan === 'Preferred' ? '#1565c0' : detail.summary.service_plan === 'Basic' ? '#2e7d32' : '#616161',
                      color: '#fff'
                    }} />
                  ) : (
                    <Typography variant="body1" color="text.secondary">No plan</Typography>
                  )}
                </Grid>
              </Grid>
            </Paper>

            {/* Site info */}
            {detail.sites?.length > 0 && (
              <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>📍 Service Sites</Typography>
                {detail.sites.map(s => (
                  <Box key={s.SERVSITENBR} sx={{ mb: 1, p: 1, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight="medium">{s.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{[s.address, s.city, s.state, s.zip].filter(Boolean).join(', ')}</Typography>
                    {s.contact1_name && <Typography variant="caption" display="block">{s.contact1_name} {s.contact1_email && `· ${s.contact1_email}`} {s.contact1_phone && `· ${s.contact1_phone}`}</Typography>}
                    {s.phone && <Typography variant="caption" display="block">📞 {s.phone}</Typography>}
                  </Box>
                ))}
              </Paper>
            )}

            <Grid container spacing={2} sx={{ mb: 2 }}>
              {/* Problem patterns */}
              <Grid item xs={12} sm={6}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>🔧 Recurring Issues (from service notes)</Typography>
                  {detail.problems?.map(p => (
                    <Box key={p.problem} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                      <Typography variant="body2" fontWeight="medium">{p.problem}</Typography>
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <Chip label={`${p.cnt} calls`} size="small" color="primary" variant="outlined" />
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40, textAlign: 'right' }}>{fmtNum(p.hours || 0)}h</Typography>
                        <Typography variant="caption" fontWeight="bold" color="success.main" sx={{ minWidth: 55, textAlign: 'right' }}>{fmtCurrency(p.billed)}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Paper>
              </Grid>
              {/* Tech history */}
              <Grid item xs={12} sm={6}>
                <Paper elevation={1} sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>👷 Technician History</Typography>
                  {detail.techs?.map(t => (
                    <Box key={t.tech_name} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.3 }}>
                      <Typography variant="body2">{t.tech_name}</Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip label={`${t.visit_count} visits`} size="small" variant="outlined" />
                        <Typography variant="caption" color="text.secondary">{fmtNum(t.total_hours)}h</Typography>
                      </Box>
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </Grid>

            {/* All work orders */}
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>📋 Work Order History ({detail.workOrders?.length || 0})</Typography>
              <Table size="small">
                <TableHead><TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700 }}>WO#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Service Notes</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Tech</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Hours</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {(detail.workOrders || []).map(w => (
                    <React.Fragment key={w.WRKORDNBR}>
                      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setSelected(s => s === `wo-${w.WRKORDNBR}` ? detail.summary.ar_code : `wo-${w.WRKORDNBR}`)}>
                        <TableCell><Typography variant="body2" color="primary.main" fontWeight="bold">{w.WRKORDNBR}</Typography></TableCell>
                        <TableCell>{fmtDate(w.DATEENTER)}</TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ maxWidth: 300, display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden' }}>
                            {(w.notes || '').trim() || w.call_type || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell><Typography variant="caption">{w.tech_name || '—'}</Typography></TableCell>
                        <TableCell align="right">{w.WRKORDAMT > 0 ? fmtCurrency(w.WRKORDAMT) : '—'}</TableCell>
                        <TableCell align="right">{w.ACTLABORHOURS > 0 ? fmtNum(w.ACTLABORHOURS) : '—'}</TableCell>
                        <TableCell><StatusChip status={w.STATUS} /></TableCell>
                      </TableRow>
                      {selected === `wo-${w.WRKORDNBR}` && w.notes && (
                        <TableRow><TableCell colSpan={8} sx={{ bgcolor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', p: 1 }}>{(w.notes || '').trim()}</Typography>
                        </TableCell></TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <PageSearchBar
        placeholder="Search client name or AR code…"
        value={search}
        onChange={(v) => setSearch(v)}
        sx={{ mb: 2 }}
      />

      {loading ? <LinearProgress /> : (
        <TableContainer component={Paper} elevation={2}>
          <Table size="small">
            <TableHead><TableRow sx={{ bgcolor: '#0D2C54' }}>
              <TableCell sx={thStyle}>{sl('client_name', 'Client')}</TableCell>
              <TableCell sx={thStyle}>{sl('ar_code', 'AR Code')}</TableCell>
              <TableCell sx={thStyle}>{sl('site_address', 'Address')}</TableCell>
              <TableCell sx={thStyle} align="center">{sl('wo_count', 'Total WOs')}</TableCell>
              <TableCell sx={thStyle} align="center">{sl('open_count', 'Open')}</TableCell>
              <TableCell sx={thStyle} align="right">{sl('total_billed', 'Total Billed')}</TableCell>
              <TableCell sx={thStyle} align="right">{sl('total_hours', 'Hours')}</TableCell>
              <TableCell sx={thStyle}>{sl('last_visit', 'Last Visit')}</TableCell>
              <TableCell sx={thStyle}>{sl('service_plan', 'Plan')}</TableCell>
              <TableCell sx={thStyle}>{sl('last_note', 'Last Service Note')}</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {sorted.map((c, i) => (
                <TableRow key={c.ar_code} hover onClick={() => loadDetail(c.ar_code)}
                  sx={{ cursor: 'pointer', bgcolor: i % 2 === 0 ? undefined : 'action.hover' }}>
                  <TableCell><Typography variant="body2" fontWeight="medium">{c.client_name}</Typography></TableCell>
                  <TableCell><Chip label={c.ar_code} size="small" variant="outlined" /></TableCell>
                  <TableCell><Typography variant="caption" noWrap sx={{ maxWidth: 200 }}>{[c.site_address, c.site_city, c.site_state].filter(Boolean).join(', ')}</Typography></TableCell>
                  <TableCell align="center"><Chip label={c.wo_count} size="small" color="primary" /></TableCell>
                  <TableCell align="center">{c.open_count > 0 ? <Chip label={c.open_count} size="small" color="warning" /> : '—'}</TableCell>
                  <TableCell align="right"><Typography variant="body2" fontWeight={c.total_billed > 0 ? 'bold' : 'normal'} color={c.total_billed > 0 ? 'success.main' : 'text.secondary'}>{c.total_billed > 0 ? fmtCurrency(c.total_billed) : '—'}</Typography></TableCell>
                  <TableCell align="right">{c.total_hours > 0 ? fmtNum(c.total_hours) : '—'}</TableCell>
                  <TableCell>{fmtDate(c.last_visit)}</TableCell>
                  <TableCell>
                    {c.service_plan ? (
                      <Chip label={c.service_plan} size="small" sx={{
                        fontWeight: 700, fontSize: '0.7rem',
                        bgcolor: c.service_plan === 'VIP' ? '#7b1fa2' : c.service_plan === 'Preferred' ? '#1565c0' : c.service_plan === 'Basic' ? '#2e7d32' : c.service_plan === 'Monitoring' ? '#00695c' : '#616161',
                        color: '#fff'
                      }} />
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell><Typography variant="caption" sx={{ maxWidth: 220, display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden' }}>{c.last_note || '—'}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ─── Technicians Tab ──────────────────────────────────────────────────────────
function TechniciansView() {
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ col: 'total_wos_ytd', dir: 'desc' });

  useEffect(() => {
    setLoading(true);
    getAftercareTechnicians()
      .then(r => setTechs(r.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSort = (col) => {
    setSort(s => ({ col, dir: s.col === col && s.dir === 'asc' ? 'desc' : 'asc' }));
  };

  const sorted = [...techs].sort((a, b) => {
    let av = a[sort.col], bv = b[sort.col];
    if (typeof av === 'string') { av = av.toLowerCase(); bv = (bv || '').toLowerCase(); }
    else { av = parseFloat(av) || 0; bv = parseFloat(bv) || 0; }
    return sort.dir === 'asc' ? (av < bv ? -1 : av > bv ? 1 : 0) : (av > bv ? -1 : av < bv ? 1 : 0);
  });

  const thStyle = { fontWeight: 700, fontSize: '0.75rem', color: '#fff' };
  const sl = (col, label) => (
    <TableSortLabel active={sort.col === col} direction={sort.col === col ? sort.dir : 'asc'} onClick={() => handleSort(col)}
      sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#ccc !important' } }}>{label}</TableSortLabel>
  );

  if (loading) return <LinearProgress />;

  return (
    <TableContainer component={Paper} elevation={2}>
      <Table size="small">
        <TableHead><TableRow sx={{ bgcolor: '#0D2C54' }}>
          <TableCell sx={thStyle}>{sl('tech_name', 'Technician')}</TableCell>
          <TableCell sx={thStyle}>Title</TableCell>
          <TableCell sx={thStyle} align="center">{sl('total_wos_ytd', 'WOs YTD')}</TableCell>
          <TableCell sx={thStyle} align="center">{sl('open_wos', 'Open')}</TableCell>
          <TableCell sx={thStyle} align="center">{sl('closed_wos_ytd', 'Closed')}</TableCell>
          <TableCell sx={thStyle} align="right">{sl('revenue_ytd', 'Revenue YTD')}</TableCell>
          <TableCell sx={thStyle} align="right">{sl('hours_ytd', 'Hours YTD')}</TableCell>
          <TableCell sx={thStyle} align="center">{sl('unique_clients', 'Clients')}</TableCell>
          <TableCell sx={thStyle}>Last WO</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {sorted.map(t => (
            <TableRow key={t.tech_id} hover>
              <TableCell><Typography variant="body2" fontWeight="medium">{t.tech_name}</Typography></TableCell>
              <TableCell><Typography variant="caption" color="text.secondary">{t.job_title || '—'}</Typography></TableCell>
              <TableCell align="center"><Chip label={t.total_wos_ytd} size="small" color="primary" /></TableCell>
              <TableCell align="center">{t.open_wos > 0 ? <Chip label={t.open_wos} size="small" color="warning" /> : '—'}</TableCell>
              <TableCell align="center">{t.closed_wos_ytd}</TableCell>
              <TableCell align="right"><Typography variant="body2" fontWeight="bold" color="success.main">{fmtCurrency(t.revenue_ytd)}</Typography></TableCell>
              <TableCell align="right">{fmtNum(t.hours_ytd)}</TableCell>
              <TableCell align="center">{t.unique_clients}</TableCell>
              <TableCell>{fmtDate(t.last_wo_date)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Coverage Tab ─────────────────────────────────────────────────────
function CoverageView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState({ col: 'client_name', dir: 'asc' });

  useEffect(() => {
    setLoading(true);
    getAftercareCoverage().then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <LinearProgress />;
  if (!data) return <Alert severity="error">Failed to load coverage data</Alert>;

  const handleSort = (col) => setSort(s => ({ col, dir: s.col === col && s.dir === 'asc' ? 'desc' : 'asc' }));

  let filtered = data.clients;
  if (search) {
    const s = search.toLowerCase();
    filtered = filtered.filter(c => (c.client_name || '').toLowerCase().includes(s) || (c.ar_code || '').toLowerCase().includes(s));
  }
  if (filter === 'plan') filtered = filtered.filter(c => c.service_plan);
  else if (filter === 'warranty') filtered = filtered.filter(c => c.projects.some(p => p.warranty_status === 'active'));
  else if (filter === 'expiring') filtered = filtered.filter(c => c.projects.some(p => p.warranty_status === 'expiring'));
  else if (filter === 'expired') filtered = filtered.filter(c => c.projects.some(p => p.warranty_status === 'expired'));
  else if (filter === 'none') filtered = filtered.filter(c => !c.service_plan && !c.projects.some(p => p.warranty_status));

  const sorted = [...filtered].sort((a, b) => {
    let av, bv;
    if (sort.col === 'total_service_wos') { av = a.total_service_wos || 0; bv = b.total_service_wos || 0; }
    else if (sort.col === 'last_service_date') { av = a.last_service_date ? new Date(a.last_service_date).getTime() : 0; bv = b.last_service_date ? new Date(b.last_service_date).getTime() : 0; }
    else if (sort.col === 'projects') { av = a.projects.length; bv = b.projects.length; }
    else { av = (a[sort.col] || '').toString().toLowerCase(); bv = (b[sort.col] || '').toString().toLowerCase(); }
    if (av < bv) return sort.dir === 'asc' ? -1 : 1;
    if (av > bv) return sort.dir === 'asc' ? 1 : -1;
    return 0;
  });

  const s = data.summary;
  const sl = (col, label) => (
    <TableSortLabel active={sort.col === col} direction={sort.col === col ? sort.dir : 'asc'} onClick={() => handleSort(col)}
      sx={{ color: '#fff !important', '& .MuiTableSortLabel-icon': { color: '#ccc !important' } }}>{label}</TableSortLabel>
  );

  const PLAN_COLORS = { VIP: '#7b1fa2', Preferred: '#1565c0', Essential: '#00695c', Basic: '#2e7d32', Monitoring: '#00838f' };

  return (
    <Box>
      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={2.4}>
          <SummaryCard label="Total Clients" value={s.total} color="#0D2C54" onClick={() => setFilter('all')} />
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <SummaryCard label="On Service Plan" value={s.withPlan} color="#7b1fa2" onClick={() => setFilter('plan')} />
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <SummaryCard label="Under Warranty" value={s.withWarranty} color="#2e7d32" onClick={() => setFilter('warranty')} />
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <SummaryCard label="Warranty Expiring" value={s.expiring} color="#e65100" onClick={() => setFilter('expiring')} />
        </Grid>
        <Grid item xs={6} sm={2.4}>
          <SummaryCard label="No Coverage" value={s.noCoverage} color="#c62828" onClick={() => setFilter('none')} />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <PageSearchBar
          placeholder="Search client or AR code…"
          value={search}
          onChange={(v) => setSearch(v)}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Filter</InputLabel>
          <Select value={filter} label="Filter" onChange={e => setFilter(e.target.value)}>
            <MenuItem value="all">All Clients ({s.total})</MenuItem>
            <MenuItem value="plan">On Service Plan ({s.withPlan})</MenuItem>
            <MenuItem value="warranty">Under Warranty ({s.withWarranty})</MenuItem>
            <MenuItem value="expiring">Warranty Expiring ({s.expiring})</MenuItem>
            <MenuItem value="expired">Warranty Expired ({s.expired})</MenuItem>
            <MenuItem value="none">No Coverage ({s.noCoverage})</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">{sorted.length} clients</Typography>
      </Box>

      <TableContainer component={Paper} elevation={2}>
        <Table size="small">
          <TableHead><TableRow sx={{ bgcolor: '#0D2C54', '& th': { color: '#fff', fontWeight: 700, fontSize: '0.75rem' } }}>
            <TableCell>{sl('client_name', 'Client')}</TableCell>
            <TableCell>{sl('ar_code', 'AR Code')}</TableCell>
            <TableCell>{sl('service_plan', 'Service Plan')}</TableCell>
            <TableCell>Warranty</TableCell>
            <TableCell align="center">{sl('projects', 'Projects')}</TableCell>
            <TableCell align="center">{sl('total_service_wos', 'Service WOs')}</TableCell>
            <TableCell>{sl('last_service_date', 'Last Service')}</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {sorted.map((c, i) => {
              const activeWarranty = c.projects.find(p => p.warranty_status === 'active' || p.warranty_status === 'expiring');
              const expiredWarranty = !activeWarranty && c.projects.find(p => p.warranty_status === 'expired');
              return (
                <TableRow key={c.ar_code} hover sx={{ bgcolor: i % 2 === 0 ? undefined : 'action.hover' }}>
                  <TableCell><Typography variant="body2" fontWeight="medium">{c.client_name}</Typography></TableCell>
                  <TableCell><Chip label={c.ar_code} size="small" variant="outlined" /></TableCell>
                  <TableCell>
                    {c.service_plan ? (
                      <Chip label={c.service_plan} size="small" sx={{ fontWeight: 700, fontSize: '0.7rem', bgcolor: PLAN_COLORS[c.service_plan] || '#616161', color: '#fff' }} />
                    ) : (
                      <Typography variant="caption" color="error.main">No plan</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {activeWarranty ? (
                      <Chip
                        label={activeWarranty.warranty_status === 'expiring'
                          ? `${activeWarranty.warranty_days_left}d left!`
                          : `${Math.ceil(activeWarranty.warranty_days_left / 30)}mo left`}
                        size="small"
                        sx={{
                          fontWeight: 600, fontSize: '0.7rem',
                          bgcolor: activeWarranty.warranty_status === 'expiring' ? '#fff3e0' : '#e8f5e9',
                          color: activeWarranty.warranty_status === 'expiring' ? '#e65100' : '#2e7d32',
                        }}
                      />
                    ) : expiredWarranty ? (
                      <Chip label="Expired" size="small" sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: '#ffebee', color: '#c62828' }} />
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">{c.projects.length}</TableCell>
                  <TableCell align="center">{c.total_service_wos || '—'}</TableCell>
                  <TableCell>{fmtDate(c.last_service_date)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ─── Main AfterCare Component ─────────────────────────────────────────────────
// Client Warranty Tab
function ClientWarrantyView() {
  const { user } = useAuth();
  const role = user?.role || '';
  const isMgmt = role === 'Management';
  const isFinance = role === 'Finance' || user?.financialAccess === true;
  const isPM = role === 'PM';
  // Permission gates aligned with API:
  const canEditEquipment = isFinance || isMgmt;
  const canEditAfterCare = isPM || isMgmt;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('client_name');
  const [sortDir, setSortDir] = useState('asc');
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState({});
  const [saving, setSaving] = useState({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getClientWarranty();
      setData(res.data);
      setErr(null);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const toggleExpand = (ar) => setExpanded(prev => ({ ...prev, [ar]: !prev[ar] }));

  const startEdit = (ar, field, currentValue) => {
    setEditing(prev => ({ ...prev, [`${ar}:${field}`]: currentValue ?? '' }));
  };
  const cancelEdit = (ar, field) => {
    setEditing(prev => {
      const n = { ...prev };
      delete n[`${ar}:${field}`];
      return n;
    });
  };
  const setEditValue = (ar, field, value) => {
    setEditing(prev => ({ ...prev, [`${ar}:${field}`]: value }));
  };
  // AfterCare warranty fields hit a separate endpoint with separate role gating.
  const AFTERCARE_FIELDS = new Set(['ac_turnover_date', 'ac_warranty_months', 'ac_warranty_status', 'ac_notes']);

  const saveEdit = async (ar, field) => {
    const key = `${ar}:${field}`;
    const value = editing[key];
    try {
      setSaving(prev => ({ ...prev, [key]: true }));
      if (AFTERCARE_FIELDS.has(field)) {
        const apiField = {
          ac_turnover_date: 'turnover_date',
          ac_warranty_months: 'warranty_months',
          ac_warranty_status: 'warranty_status_override',
          ac_notes: 'notes',
        }[field];
        const payload = { [apiField]: value === '' ? null : value };
        await updateAfterCareWarranty(ar, payload);
      } else {
        const apiField = {
          client_name: 'client_name_override',
          site_address: 'site_address_override',
          warranty_start_date: 'warranty_start_date',
          warranty_status: 'warranty_status_override',
        }[field] || field;
        const payload = { [apiField]: value === '' ? null : value };
        await updateClientWarranty(ar, payload);
      }
      cancelEdit(ar, field);
      await load();
    } catch (e) {
      alert('Save failed: ' + (e.response?.data?.error || e.response?.data?.detail || e.message));
    } finally {
      setSaving(prev => { const n = { ...prev }; delete n[key]; return n; });
    }
  };

  if (loading && !data) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  if (err) return <Alert severity="error">Failed to load client warranty data: {err}</Alert>;
  if (!data) return null;

  let rows = data.clients;
  if (search) {
    const s = search.toLowerCase();
    rows = rows.filter(c =>
      (c.client_name || '').toLowerCase().includes(s) ||
      (c.ar_customer || '').toLowerCase().includes(s) ||
      (c.site_address || '').toLowerCase().includes(s)
    );
  }
  if (statusFilter !== 'all') {
    if (statusFilter.startsWith('ac:')) {
      const want = statusFilter.slice(3);
      rows = rows.filter(c => c.aftercare_warranty?.status === want);
    } else {
      rows = rows.filter(c => c.warranty_status === statusFilter);
    }
  }
  rows = [...rows].sort((a, b) => {
    let av = a[sortBy], bv = b[sortBy];
    if (sortBy === 'warranty_start_date') { av = new Date(av); bv = new Date(bv); }
    if (sortBy === 'other_jobs_count') { av = Number(av) || 0; bv = Number(bv) || 0; }
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const EditableCell = ({ ar, field, value, type = 'text', editable = true, options = null }) => {
    const key = `${ar}:${field}`;
    const isEditing = Object.prototype.hasOwnProperty.call(editing, key);
    const isSaving = !!saving[key];
    if (!isEditing) {
      const handler = editable ? () => startEdit(ar, field, value) : undefined;
      return (
        <Box onDoubleClick={handler} sx={{ cursor: editable ? 'pointer' : 'default', minHeight: 24, display: 'flex', alignItems: 'center' }} title={editable ? 'Double-click to edit' : 'Read-only for your role'}>
          {value || <Typography variant="caption" color="text.disabled">—</Typography>}
        </Box>
      );
    }
    if (options) {
      return (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select value={editing[key] ?? ''} onChange={e => setEditValue(ar, field, e.target.value)} displayEmpty>
              {options.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
            </Select>
          </FormControl>
          <IconButton size="small" onClick={() => saveEdit(ar, field)} disabled={isSaving}>✓</IconButton>
          <IconButton size="small" onClick={() => cancelEdit(ar, field)} disabled={isSaving}>✕</IconButton>
        </Box>
      );
    }
    if (field === 'warranty_status') {
      return (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select value={editing[key] ?? ''} onChange={e => setEditValue(ar, field, e.target.value)} displayEmpty>
              <MenuItem value=""><em>Auto</em></MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
            </Select>
          </FormControl>
          <IconButton size="small" onClick={() => saveEdit(ar, field)} disabled={isSaving}>✓</IconButton>
          <IconButton size="small" onClick={() => cancelEdit(ar, field)} disabled={isSaving}>✕</IconButton>
        </Box>
      );
    }
    return (
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <TextField
          size="small"
          type={type}
          value={editing[key] ?? ''}
          onChange={e => setEditValue(ar, field, e.target.value)}
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter') saveEdit(ar, field); if (e.key === 'Escape') cancelEdit(ar, field); }}
          sx={{ minWidth: type === 'date' ? 150 : 200 }}
        />
        <IconButton size="small" onClick={() => saveEdit(ar, field)} disabled={isSaving}>✓</IconButton>
        <IconButton size="small" onClick={() => cancelEdit(ar, field)} disabled={isSaving}>✕</IconButton>
      </Box>
    );
  };

  // Status chip for AfterCare warranty (4 states)
  const StatusChipAfterCare = ({ status }) => {
    const map = {
      active:   { label: 'Active',   bg: '#e8f5e9', fg: '#2e7d32' },
      expiring: { label: 'Expiring', bg: '#fff3e0', fg: '#e65100' },
      expired:  { label: 'Expired',  bg: '#ffebee', fg: '#c62828' },
      not_set:  { label: 'Not Set',  bg: '#eceff1', fg: '#546e7a' },
    };
    const m = map[status] || map.not_set;
    return <Chip label={m.label} size="small" sx={{ fontWeight: 700, fontSize: '0.72rem', bgcolor: m.bg, color: m.fg }} />;
  };

  const StatusChipWarranty = ({ status }) => (
    <Chip
      label={status === 'active' ? 'Active' : 'Expired'}
      size="small"
      sx={{
        fontWeight: 700, fontSize: '0.72rem',
        bgcolor: status === 'active' ? '#e8f5e9' : '#ffebee',
        color: status === 'active' ? '#2e7d32' : '#c62828',
      }}
    />
  );

  return (
    <Box>
      {/* Equipment (Manufacturer) Warranty — Finance owned */}
      <Typography variant="overline" sx={{ color: '#1565c0', fontWeight: 700, letterSpacing: 1 }}>
        Equipment Warranty (Manufacturer — Finance owns)
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2, mt: 0.5 }}>
        <Grid item xs={12} sm={3}>
          <SummaryCard label="Total Clients" value={data.summary.total_clients} color="#0D2C54" onClick={() => setStatusFilter('all')} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <SummaryCard label="Equipment Active" value={data.summary.equipment?.active ?? data.summary.active} color="#2e7d32" onClick={() => setStatusFilter('active')} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <SummaryCard label="Equipment Expired" value={data.summary.equipment?.expired ?? data.summary.expired} color="#c62828" onClick={() => setStatusFilter('expired')} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <SummaryCard label="Jobs Since 2020" value={data.summary.total_jobs} color="#1565c0" sub={`cutoff ${data.summary.cutoff}`} />
        </Grid>
      </Grid>

      {/* AfterCare Service Warranty — PM owned */}
      <Typography variant="overline" sx={{ color: '#6a1b9a', fontWeight: 700, letterSpacing: 1 }}>
        AfterCare Service Warranty (Labor + Monitoring — PMs own)
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3, mt: 0.5 }}>
        <Grid item xs={12} sm={3}>
          <SummaryCard label="AfterCare Active" value={data.summary.aftercare?.active ?? 0} color="#2e7d32" onClick={() => setStatusFilter('ac:active')} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <SummaryCard label="Expiring (<30d)" value={data.summary.aftercare?.expiring ?? 0} color="#e65100" onClick={() => setStatusFilter('ac:expiring')} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <SummaryCard label="AfterCare Expired" value={data.summary.aftercare?.expired ?? 0} color="#c62828" onClick={() => setStatusFilter('ac:expired')} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <SummaryCard label="Not Set (action needed)" value={data.summary.aftercare?.not_set ?? 0} color="#546e7a" onClick={() => setStatusFilter('ac:not_set')} sub="PMs to set turnover" />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <PageSearchBar
          placeholder="Search AR code, client, or site address…"
          value={search}
          onChange={(v) => setSearch(v)}
        />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Filter</InputLabel>
          <Select value={statusFilter} label="Filter" onChange={e => setStatusFilter(e.target.value)}>
            <MenuItem value="all">All ({data.summary.total_clients})</MenuItem>
            <MenuItem disabled><em>— Equipment Warranty —</em></MenuItem>
            <MenuItem value="active">Equipment Active ({data.summary.equipment?.active ?? data.summary.active})</MenuItem>
            <MenuItem value="expired">Equipment Expired ({data.summary.equipment?.expired ?? data.summary.expired})</MenuItem>
            <MenuItem disabled><em>— AfterCare Warranty —</em></MenuItem>
            <MenuItem value="ac:active">AfterCare Active ({data.summary.aftercare?.active ?? 0})</MenuItem>
            <MenuItem value="ac:expiring">AfterCare Expiring ({data.summary.aftercare?.expiring ?? 0})</MenuItem>
            <MenuItem value="ac:expired">AfterCare Expired ({data.summary.aftercare?.expired ?? 0})</MenuItem>
            <MenuItem value="ac:not_set">AfterCare Not Set ({data.summary.aftercare?.not_set ?? 0})</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
          Showing {rows.length} of {data.summary.total_clients}. Double-click any value to edit.
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f5f5f5' } }}>
              <TableCell />
              <TableCell sortDirection={sortBy === 'ar_customer' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'ar_customer'} direction={sortBy === 'ar_customer' ? sortDir : 'asc'} onClick={() => handleSort('ar_customer')}>
                  AR Number
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'client_name' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'client_name'} direction={sortBy === 'client_name' ? sortDir : 'asc'} onClick={() => handleSort('client_name')}>
                  Client Name
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'site_address' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'site_address'} direction={sortBy === 'site_address' ? sortDir : 'asc'} onClick={() => handleSort('site_address')}>
                  Site Address
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sortDirection={sortBy === 'other_jobs_count' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'other_jobs_count'} direction={sortBy === 'other_jobs_count' ? sortDir : 'asc'} onClick={() => handleSort('other_jobs_count')}>
                  Other Jobs
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ borderLeft: '3px solid #1565c0' }} sortDirection={sortBy === 'warranty_status' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'warranty_status'} direction={sortBy === 'warranty_status' ? sortDir : 'asc'} onClick={() => handleSort('warranty_status')}>
                  Equipment Warranty
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'warranty_start_date' ? sortDir : false}>
                <TableSortLabel active={sortBy === 'warranty_start_date'} direction={sortBy === 'warranty_start_date' ? sortDir : 'asc'} onClick={() => handleSort('warranty_start_date')}>
                  Equip Start
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ borderLeft: '3px solid #6a1b9a' }}>AfterCare Warranty</TableCell>
              <TableCell>Turnover Date</TableCell>
              <TableCell align="center">Months</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(c => {
              const isOpen = !!expanded[c.ar_customer];
              return (
                <React.Fragment key={c.ar_customer}>
                  <TableRow hover>
                    <TableCell sx={{ width: 40 }}>
                      <IconButton size="small" onClick={() => toggleExpand(c.ar_customer)}>
                        {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{c.ar_customer}</TableCell>
                    <TableCell><EditableCell ar={c.ar_customer} field="client_name" value={c.client_name} /></TableCell>
                    <TableCell sx={{ maxWidth: 300 }}><EditableCell ar={c.ar_customer} field="site_address" value={c.site_address} /></TableCell>
                    <TableCell align="center">
                      <Chip label={c.other_jobs_count} size="small" sx={{ fontWeight: 600 }} onClick={() => toggleExpand(c.ar_customer)} />
                    </TableCell>
                    {/* Equipment warranty (Finance) */}
                    <TableCell sx={{ borderLeft: '3px solid #1565c0' }}>
                      {Object.prototype.hasOwnProperty.call(editing, `${c.ar_customer}:warranty_status`)
                        ? <EditableCell ar={c.ar_customer} field="warranty_status" value={c.warranty_status} editable={canEditEquipment} />
                        : (
                          <Box onDoubleClick={canEditEquipment ? () => startEdit(c.ar_customer, 'warranty_status', '') : undefined} sx={{ cursor: canEditEquipment ? 'pointer' : 'default' }} title={canEditEquipment ? 'Double-click to override' : 'Read-only for your role'}>
                            <StatusChipWarranty status={c.warranty_status} />
                            {' '}
                            <Typography variant="caption" color="text.secondary">
                              {c.warranty_status === 'active' ? `${c.warranty_days_left}d left` : `ended ${fmtDate(c.warranty_end_date)}`}
                            </Typography>
                          </Box>
                        )}
                    </TableCell>
                    <TableCell><EditableCell ar={c.ar_customer} field="warranty_start_date" value={c.warranty_start_date} type="date" editable={canEditEquipment} /></TableCell>

                    {/* AfterCare warranty (PM) */}
                    <TableCell sx={{ borderLeft: '3px solid #6a1b9a' }}>
                      {Object.prototype.hasOwnProperty.call(editing, `${c.ar_customer}:ac_warranty_status`)
                        ? <EditableCell ar={c.ar_customer} field="ac_warranty_status" value={c.aftercare_warranty?.status} editable={canEditAfterCare}
                            options={[
                              { value: '', label: 'Auto' },
                              { value: 'active', label: 'Active' },
                              { value: 'expiring', label: 'Expiring' },
                              { value: 'expired', label: 'Expired' },
                              { value: 'not_set', label: 'Not Set' },
                            ]}
                          />
                        : (
                          <Box onDoubleClick={canEditAfterCare ? () => startEdit(c.ar_customer, 'ac_warranty_status', '') : undefined} sx={{ cursor: canEditAfterCare ? 'pointer' : 'default' }} title={canEditAfterCare ? 'Double-click to override' : 'Only PMs and Management can edit AfterCare warranty'}>
                            <StatusChipAfterCare status={c.aftercare_warranty?.status || 'not_set'} />
                            {' '}
                            <Typography variant="caption" color="text.secondary">
                              {c.aftercare_warranty?.status === 'active' || c.aftercare_warranty?.status === 'expiring'
                                ? `${c.aftercare_warranty.days_left}d left`
                                : c.aftercare_warranty?.status === 'expired'
                                  ? `ended ${fmtDate(c.aftercare_warranty.end_date)}`
                                  : ''}
                            </Typography>
                          </Box>
                        )}
                    </TableCell>
                    <TableCell>
                      <EditableCell ar={c.ar_customer} field="ac_turnover_date" value={c.aftercare_warranty?.turnover_date} type="date" editable={canEditAfterCare} />
                    </TableCell>
                    <TableCell align="center">
                      <EditableCell ar={c.ar_customer} field="ac_warranty_months" value={String(c.aftercare_warranty?.warranty_months ?? 12)} editable={canEditAfterCare}
                        options={[
                          { value: '12', label: '12 months' },
                          { value: '24', label: '24 months' },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={10} sx={{ p: 0, borderBottom: isOpen ? undefined : 'none' }}>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                              <Typography variant="subtitle2" sx={{ mb: 1, color: '#0D2C54' }}>Contact Information</Typography>
                              <Box sx={{ fontSize: '0.85rem', lineHeight: 1.8 }}>
                                <div><b>Primary:</b> {c.contact.name || '—'}</div>
                                {c.contact.secondary && <div><b>Secondary:</b> {c.contact.secondary}</div>}
                                <div><b>Phone:</b> {c.contact.phone ? <a href={`tel:${c.contact.phone}`}>{c.contact.phone}</a> : '—'}</div>
                                <div><b>Email:</b> {c.contact.email ? <a href={`mailto:${c.contact.email}`}>{c.contact.email}</a> : '—'}</div>
                                <div><b>Billing:</b> {c.contact.billing_address || '—'}</div>
                                <div><b>Customer status:</b> {c.contact.customer_status || '—'}</div>
                                <div><b>Established:</b> {fmtDate(c.contact.date_established)}</div>
                              </Box>
                            </Grid>
                            <Grid item xs={12} md={8}>
                              <Typography variant="subtitle2" sx={{ mb: 1, color: '#0D2C54' }}>All Jobs ({c.jobs.length})</Typography>
                              <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ '& th': { fontWeight: 600, fontSize: '0.75rem' } }}>
                                      <TableCell>Job</TableCell>
                                      <TableCell>Description</TableCell>
                                      <TableCell>Status</TableCell>
                                      <TableCell>PM</TableCell>
                                      <TableCell>Revised Comp</TableCell>
                                      <TableCell>Warranty</TableCell>
                                      <TableCell align="right">Contract</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {[...c.jobs].sort((a, b) => new Date(b.revised_comp) - new Date(a.revised_comp)).map(j => {
                                      const jStart = new Date(j.revised_comp);
                                      const jMonths = j.two_year_warranty ? 24 : 12;
                                      const jEnd = new Date(jStart);
                                      jEnd.setMonth(jEnd.getMonth() + jMonths);
                                      const jDaysLeft = Math.ceil((jEnd - new Date()) / 86400000);
                                      const jActive = jDaysLeft >= 0;
                                      return (
                                        <TableRow key={j.job}>
                                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{j.job}</TableCell>
                                          <TableCell sx={{ fontSize: '0.8rem' }}>{j.description}</TableCell>
                                          <TableCell sx={{ fontSize: '0.8rem' }}>{j.status}</TableCell>
                                          <TableCell sx={{ fontSize: '0.8rem' }}>{j.pm}</TableCell>
                                          <TableCell sx={{ fontSize: '0.8rem' }}>{fmtDate(j.revised_comp)}</TableCell>
                                          <TableCell sx={{ fontSize: '0.8rem' }}>
                                            <Chip
                                              label={jActive ? 'Active' : 'Expired'}
                                              size="small"
                                              sx={{
                                                fontWeight: 700, fontSize: '0.65rem', height: 20,
                                                bgcolor: jActive ? '#e8f5e9' : '#ffebee',
                                                color: jActive ? '#2e7d32' : '#c62828',
                                              }}
                                            />
                                            <Typography variant="caption" sx={{ ml: 0.75, color: 'text.secondary', fontSize: '0.7rem' }}>
                                              {jActive ? `${jDaysLeft}d left` : `ended ${fmtDate(jEnd)}`}
                                              {j.two_year_warranty ? ' · 2yr' : ''}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{fmtCurrency(j.revised_contract)}</TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                              {c.override.has_override && (
                                <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#e65100' }}>
                                  Override active — last edited {fmtDate(c.override.updated_at)}
                                </Typography>
                              )}
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ── Closed Jobs view ──────────────────────────────────────────────────────────
// Archive of completed projects. Tom Clancy 2026-05-14: when a project moves
// from WIP to completed (Sage Status=Closed or D-Tools Progress != Work In
// Progress), it disappears from the WIP page but the entire Command Center
// project record — notes, history, contacts, RACI, proposal — stays accessible
// here forever. Click a row to open the full project page.
// IntakeQueueBanner / IntakeQueueView — added 2026-05-20 (Tom Clancy).
// Running list of projects marked Complete in D-Tools awaiting AfterCare
// intake. A row drops off when someone sets a turnover_date in the warranty
// editor (the natural intake action) or dismisses the project as not-for-
// AfterCare. Dismissals live in the aftercare_intake_dismissals table and are
// reversible.
function IntakeQueueBanner({ onOpen }) {
  const [count, setCount] = useState(null);
  const [oldestDays, setOldestDays] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await getAftercareIntakeQueue({ limit: 1000 });
      const jobs = res.data?.jobs || [];
      setCount(jobs.length);
      setOldestDays(jobs.length ? Math.max(...jobs.map(j => Number(j.days_in_queue) || 0)) : 0);
    } catch (_) {
      setCount(0);
      setOldestDays(0);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (count === null) return null;       // still loading
  if (count === 0) return null;          // clean board, hide entirely

  return (
    <Alert
      severity="warning"
      icon={<MoveToInboxIcon />}
      action={
        <Button color="inherit" size="small" variant="outlined" onClick={onOpen}>
          Review Intake Queue →
        </Button>
      }
      sx={{ mb: 2 }}
    >
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {count} project{count === 1 ? '' : 's'} waiting for AfterCare intake
        </Typography>
        <Typography variant="caption" color="text.secondary">
          marked Complete in D-Tools — turnover date not set yet
          {oldestDays > 0 ? ` • oldest ${oldestDays} days waiting` : ''}
        </Typography>
      </Box>
    </Alert>
  );
}

function IntakeQueueView() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role  = user?.role || '';
  const roles = Array.isArray(user?.roles) ? user.roles : [];
  const canDismiss = role === 'Management' || role === 'PM'
    || role.includes('AfterCare')
    || roles.includes('Management') || roles.includes('PM')
    || roles.some((r) => (r || '').includes('AfterCare'));

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('days_in_queue');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const [dismissTarget, setDismissTarget] = useState(null);
  const [dismissReason, setDismissReason] = useState('');
  const [dismissing, setDismissing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAftercareIntakeQueue({ search, limit: 500 });
      setJobs(res.data?.jobs || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [search]);
  useEffect(() => { load(); }, [load]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
    setPage(1);
  };

  const sorted = [...jobs].sort((a, b) => {
    let av = a[sortBy], bv = b[sortBy];
    if (sortBy === 'completed_on') { av = av ? new Date(av).getTime() : 0; bv = bv ? new Date(bv).getTime() : 0; }
    if (sortBy === 'days_in_queue' || sortBy === 'contract_value') { av = Number(av) || 0; bv = Number(bv) || 0; }
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    const cmp = av < bv ? -1 : av > bv ? 1 : 0;
    return sortDir === 'asc' ? cmp : -cmp;
  });
  const pageRows = sorted.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));

  const doDismiss = async () => {
    if (!dismissTarget) return;
    setDismissing(true);
    try {
      await dismissAftercareIntake(dismissTarget.id, dismissReason.trim());
      setDismissTarget(null);
      setDismissReason('');
      await load();
    } catch (e) {
      alert('Dismiss failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setDismissing(false);
    }
  };

  const columns = [
    { key: 'primary_job_number', label: 'Job #' },
    { key: 'project_name',       label: 'Project' },
    { key: 'client_name',        label: 'Client' },
    { key: 'pm_name',            label: 'PM' },
    { key: 'contract_value',     label: 'Value', align: 'right' },
    { key: 'completed_on',       label: 'Completed' },
    { key: 'days_in_queue',      label: 'Days Waiting', align: 'center' },
  ];

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoveToInboxIcon /> AfterCare Intake Queue
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Projects marked Complete in D-Tools awaiting formal handoff to AfterCare.
          Set a turnover date in the warranty editor to accept, or dismiss as not-for-AfterCare.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <PageSearchBar
          placeholder="Search job#, project, client, AR, or address…"
          value={search}
          onChange={(v) => { setSearch(v); setPage(1); }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {loading ? 'Loading…' : `${sorted.length} project${sorted.length === 1 ? '' : 's'} in queue`}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!loading && sorted.length === 0 && !error && (
        <Alert severity="success">All clear — no projects waiting for AfterCare intake.</Alert>
      )}

      {sorted.length > 0 && (
        <>
          <TableContainer component={Paper}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: '#f5f5f5' } }}>
                  {columns.map((c) => (
                    <TableCell key={c.key} align={c.align || 'left'} sortDirection={sortBy === c.key ? sortDir : false}>
                      <TableSortLabel
                        active={sortBy === c.key}
                        direction={sortBy === c.key ? sortDir : 'asc'}
                        onClick={() => handleSort(c.key)}
                      >
                        {c.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.map((j) => (
                  <TableRow key={j.id} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      <Box
                        component="span"
                        onClick={() => navigate(`/projects/${j.id}`)}
                        sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                      >
                        {j.primary_job_number || '—'}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{j.name}</Typography>
                      {j.site_address && (
                        <Typography variant="caption" color="text.secondary">{j.site_address}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{j.client_name || '—'}</Typography>
                      {j.ar_number && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          AR {j.ar_number}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell><Typography variant="body2">{j.pm_name || '—'}</Typography></TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{j.contract_value != null ? fmtCurrency(j.contract_value) : '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{fmtDate(j.completed_on)}</Typography>
                      {j.sage_status && (
                        <Typography variant="caption" color="text.secondary">Sage: {j.sage_status}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={`${j.days_in_queue}d`}
                        sx={{
                          fontWeight: 700,
                          bgcolor: j.days_in_queue >= 90 ? '#ffebee'
                                 : j.days_in_queue >= 30 ? '#fff3e0'
                                 : '#e8f5e9',
                          color:   j.days_in_queue >= 90 ? '#c62828'
                                 : j.days_in_queue >= 30 ? '#e65100'
                                 : '#2e7d32',
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="Open project to set turnover date in warranty editor">
                          <span>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<SaveIcon fontSize="small" />}
                              onClick={() => navigate(`/projects/${j.id}`)}
                            >
                              Intake
                            </Button>
                          </span>
                        </Tooltip>
                        {canDismiss && (
                          <Tooltip title="Not an AfterCare project — dismiss from queue">
                            <IconButton size="small" onClick={() => { setDismissTarget(j); setDismissReason(''); }}>
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
            </Box>
          )}
        </>
      )}

      {dismissTarget && (
        <Box
          sx={{
            position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300,
          }}
          onClick={() => !dismissing && setDismissTarget(null)}
        >
          <Paper sx={{ p: 3, maxWidth: 500, width: '90%' }} onClick={(e) => e.stopPropagation()}>
            <Typography variant="h6" gutterBottom>Dismiss from intake queue?</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>{dismissTarget.primary_job_number}</strong> — {dismissTarget.name}
              <br />
              This project will no longer appear in the intake queue. The project itself stays intact.
              Dismissals are reversible (DELETE the row in aftercare_intake_dismissals).
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Reason (optional)"
              placeholder="e.g. internal upgrade, lost work, consult-only"
              value={dismissReason}
              onChange={(e) => setDismissReason(e.target.value)}
              sx={{ mb: 2 }}
              multiline
              minRows={2}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button onClick={() => setDismissTarget(null)} disabled={dismissing}>Cancel</Button>
              <Button
                onClick={doDismiss}
                color="warning"
                variant="contained"
                disabled={dismissing}
                startIcon={dismissing ? <CircularProgress size={14} /> : <BlockIcon />}
              >
                Dismiss
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}

function ClosedJobsView() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [pms, setPms] = useState([]);
  const [pmFilter, setPmFilter] = useState('');
  // Salesperson of record — surfaced 2026-05-15 at Tom's request. Backed by
  // p.sales_id / users join in /api/aftercare/closed-jobs.
  const [sales, setSales] = useState([]);
  const [salesFilter, setSalesFilter] = useState('');
  const [sortBy, setSortBy] = useState('sage_est_comp');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (pmFilter) params.pm_id = pmFilter;
      getClosedJobs(params)
        .then((r) => {
          if (cancelled) return;
          const list = r.data?.jobs || [];
          setJobs(list);
          // Build PM dropdown from this result set + always include the
          // already-selected PM so the filter doesn't disappear after applying.
          const seenPm = new Map();
          for (const j of list) if (j.pm_id && j.pm_name) seenPm.set(j.pm_id, j.pm_name);
          setPms(Array.from(seenPm, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)));
          // Same for Salesperson.
          const seenSales = new Map();
          for (const j of list) if (j.sales_id && j.sales_name) seenSales.set(j.sales_id, j.sales_name);
          setSales(Array.from(seenSales, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)));
        })
        .catch((e) => !cancelled && setError(e.response?.data?.detail || e.message))
        .finally(() => !cancelled && setLoading(false));
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search, pmFilter]);

  // Salesperson filter is client-side (API doesn't have a sales_id param yet —
  // can add when the dataset grows beyond a few hundred closed jobs).
  const filtered = salesFilter
    ? jobs.filter((j) => String(j.sales_id) === String(salesFilter))
    : jobs;

  const sorted = [...filtered].sort((a, b) => {
    const av = a[sortBy] ?? '';
    const bv = b[sortBy] ?? '';
    if (sortBy === 'total_proposal_value') {
      return (sortDir === 'asc' ? 1 : -1) * ((parseFloat(av) || 0) - (parseFloat(bv) || 0));
    }
    return (sortDir === 'asc' ? 1 : -1) * String(av).localeCompare(String(bv));
  });

  const handleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(key); setSortDir('desc'); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 700, color: '#0D2C54' }}>
        Closed Jobs
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        Projects marked complete in D-Tools / Sage. Their full Command Center page — notes, history, contacts, RACI, proposal, Dropbox links — stays accessible here. Click any row to open the project.
      </Typography>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <PageSearchBar
          placeholder="Search by job #, name, client, address…"
          value={search}
          onChange={(v) => setSearch(v)}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Project Manager</InputLabel>
          <Select value={pmFilter} label="Project Manager" onChange={(e) => setPmFilter(e.target.value)}>
            <MenuItem value="">All PMs</MenuItem>
            {pms.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Salesperson</InputLabel>
          <Select value={salesFilter} label="Salesperson" onChange={(e) => setSalesFilter(e.target.value)}>
            <MenuItem value="">All Sales</MenuItem>
            {sales.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {loading ? 'Loading…' : `${sorted.length} closed job${sorted.length === 1 ? '' : 's'}${salesFilter || pmFilter ? ` (filtered from ${jobs.length})` : ''}`}
        </Typography>
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel active={sortBy === 'primary_job_number'} direction={sortDir} onClick={() => handleSort('primary_job_number')}>Job #</TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel active={sortBy === 'name'} direction={sortDir} onClick={() => handleSort('name')}>Project</TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel active={sortBy === 'client_name'} direction={sortDir} onClick={() => handleSort('client_name')}>Client</TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>AR</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Site</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel active={sortBy === 'pm_name'} direction={sortDir} onClick={() => handleSort('pm_name')}>PM</TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel active={sortBy === 'sales_name'} direction={sortDir} onClick={() => handleSort('sales_name')}>Sales</TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">
                <TableSortLabel active={sortBy === 'sage_est_comp'} direction={sortDir} onClick={() => handleSort('sage_est_comp')}>Completed</TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {!loading && sorted.length === 0 && (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">No closed jobs match these filters.</Typography>
              </TableCell></TableRow>
            )}
            {!loading && sorted.map((j) => (
              <TableRow
                key={j.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/projects/${j.id}`)}
              >
                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{j.primary_job_number || '—'}</TableCell>
                <TableCell>{j.name}</TableCell>
                <TableCell>{j.client_name || '—'}</TableCell>
                <TableCell><Chip size="small" label={j.ar_number || '—'} sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} /></TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {[j.project_address, j.site_address].filter(Boolean).join(' — ') || '—'}
                  </Typography>
                </TableCell>
                <TableCell>{j.pm_name || '—'}</TableCell>
                <TableCell>{j.sales_name || '—'}</TableCell>
                <TableCell align="right">{fmtDate(j.sage_est_comp || j.projected_completion_date)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default function AfterCare() {
  // Tab order — Dashboard pinned, everything else A→Z (standing rule 2026-05-11).
  // 'Intake Queue' added 2026-05-20 (Tom Clancy) — projects marked Complete in
  // D-Tools that haven't been formally handed off to AfterCare yet.
  // Name-based dispatch lets us reorder without breaking deep-links.
  const TABS = [
    { key: 'dashboard',       label: 'Dashboard',       icon: <AssignmentIcon /> },
    { key: 'client-warranty', label: 'Client Warranty', icon: <VerifiedUserIcon /> },
    { key: 'clients',         label: 'Clients',         icon: <BusinessIcon /> },
    { key: 'closed-jobs',     label: 'Closed Jobs',     icon: <BusinessIcon /> },
    { key: 'coverage',        label: 'Coverage',        icon: <AssignmentIcon /> },
    { key: 'intake-queue',    label: 'Intake Queue',    icon: <MoveToInboxIcon /> },
    { key: 'operations',      label: 'Operations',      icon: <DashboardIcon /> },
    { key: 'technicians',     label: 'Technicians',     icon: <PersonIcon /> },
    { key: 'work-orders',     label: 'Work Orders',     icon: <BuildIcon /> },
  ];
  const idxOf = (key) => TABS.findIndex((t) => t.key === key);

  const [tab, setTab] = useState(0);
  const [pendingArcust, setPendingArcust] = useState(null);

  // Called from Operations tab rows to deep-link into Clients drilldown.
  const openClient = (arcust) => {
    if (!arcust) return;
    setPendingArcust(arcust);
    setTab(idxOf('clients'));
  };

  // Dashboard summary cards use this to jump to other tabs by name.
  const goTo = (key) => setTab(idxOf(key));

  const activeKey = TABS[tab]?.key;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 300, color: '#0D2C54', letterSpacing: '-0.5px' }}>AfterCare</Typography>
        <Typography variant="body2" sx={{ color: '#8898A4', mt: 0.5 }}>Service management powered by Sage SM — live data</Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, '& .MuiTab-root': { fontWeight: 600, textTransform: 'none' } }}
      >
        {TABS.map((t) => (
          <Tab key={t.key} label={t.label} icon={t.icon} iconPosition="start" />
        ))}
      </Tabs>

      {activeKey === 'dashboard'       && <DashboardView onNavigate={goTo} />}
      {activeKey === 'client-warranty' && <ClientWarrantyView />}
      {activeKey === 'clients'         && <ClientsView initialArcust={pendingArcust} onClearInitial={() => setPendingArcust(null)} />}
      {activeKey === 'closed-jobs'     && <ClosedJobsView />}
      {activeKey === 'coverage'        && <CoverageView />}
      {activeKey === 'intake-queue'    && <IntakeQueueView />}
      {activeKey === 'operations'      && <AftercareOperationsView onOpenClient={openClient} />}
      {activeKey === 'technicians'     && <TechniciansView />}
      {activeKey === 'work-orders'     && <WorkOrdersView />}
    </Box>
  );
}
